import { RequestStatus } from "@albertine/types";
import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames";
import type React from "react";
import { useState } from "react";
import { type SubmitErrorHandler, type SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { RequestStatusMap } from "../../../common/types/RequestStatus";
import Dropdown from "../../Common/Dropdown";
import EditableInputField from "../../Common/EditableInputField";
import EditableTextArea from "../../Common/EditableTextarea";
import { Block, Spacer } from "../LibraryBuildingBlocks";

function FlexRow(props: { children: React.ReactNode }) {
    const { children } = props;
    return (
        <div
            style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
            }}
        >
            {children}
        </div>
    );
}

const unselected = { key: "unselected", name: "Select" };

const ExampleFormValidationSchema = z.object({
    exampleHtmlInput: z
        .string()
        .min(1, { error: "This input is required" })
        .max(5, { error: "Should be maximum of 5 characters" }),

    exampleEditableInputField: z
        .string()
        .min(1, { error: "This input is required" })
        .max(5, { error: "Should be maximum of 5 characters" }),

    exampleEditableTextarea: z
        .string()
        .min(1, { error: "This input is required" })
        .max(5, { error: "Should be maximum of 10 characters" }),

    exampleDropdown: z.enum(RequestStatus.options, {
        error: () => "This input is required",
    }),
});

type ExampleFormValidationSchema = z.infer<typeof ExampleFormValidationSchema>;

function FormExamples() {
    const {
        register,
        handleSubmit,
        setValue,
        trigger,
        formState: { errors },
    } = useForm<ExampleFormValidationSchema>({
        resolver: zodResolver(ExampleFormValidationSchema),
    });

    const onSubmit: SubmitHandler<ExampleFormValidationSchema> = (data) => {
        console.log("This fires when the form is valid", data);
        window.alert("Form is valid and ready to be sent");
    };

    const onInvalid: SubmitErrorHandler<ExampleFormValidationSchema> = (data) => {
        console.log("This fires when to form is invalid", data);
    };

    // Holding the selected value should not be parent component responsibility, Dropdown.tsx should be refactored
    const [dropdownExampleValue, setDropdownExampleValue] = useState<string>(
        unselected.key,
    );

    return (
        <Block title="Forms - hook form example">
            <div>Look for console.logs for more information!</div>

            <Spacer />

            <form
                onSubmit={handleSubmit(onSubmit, onInvalid)}
                style={{ display: "flex", flexDirection: "column", width: 600 }}
            >
                <label htmlFor="exampleTextInput">Plain HTML input example</label>
                <FlexRow>
                    <input
                        style={{ width: 300 }}
                        type="text"
                        className={classNames({
                            "invalid-input": errors.exampleHtmlInput,
                        })}
                        {...register("exampleHtmlInput")}
                    />
                    {errors.exampleHtmlInput && (
                        <span style={{ color: "red" }}>
                            {errors.exampleHtmlInput.message}
                        </span>
                    )}
                </FlexRow>

                <Spacer />

                <FlexRow>
                    <div style={{ width: 300 }}>
                        <EditableInputField
                            label="EditableInputField example"
                            className={classNames({
                                "invalid-input": errors.exampleEditableInputField,
                            })}
                            onChange={(value) => {
                                setValue("exampleEditableInputField", value);
                                trigger(["exampleEditableInputField"]);
                            }}
                        />
                    </div>

                    {errors.exampleEditableInputField && (
                        <span style={{ color: "red" }}>
                            {errors.exampleEditableInputField.message}
                        </span>
                    )}
                </FlexRow>

                <FlexRow>
                    <div style={{ width: 300 }}>
                        <EditableTextArea
                            label="EditableTextarea example"
                            className={classNames({
                                "invalid-input": errors.exampleEditableTextarea,
                            })}
                            onChange={(value) => {
                                setValue("exampleEditableTextarea", value);
                                trigger(["exampleEditableTextarea"]);
                            }}
                        />
                    </div>
                    {errors.exampleEditableTextarea && (
                        <span style={{ color: "red" }}>
                            {errors.exampleEditableTextarea.message}
                        </span>
                    )}
                </FlexRow>

                <Spacer />

                <FlexRow>
                    <div style={{ width: 300 }}>
                        <Dropdown
                            label="Dropdown example"
                            currentValue={dropdownExampleValue}
                            entries={[
                                [unselected.key, unselected.name],
                                ...Object.entries(RequestStatusMap),
                            ]}
                            className={classNames({
                                "invalid-input": errors.exampleDropdown,
                            })}
                            onChange={(value) => {
                                const newValue = value || (unselected.key as any);

                                // This should be inside Dropdown.tsx, this makes parent render when it should not
                                setDropdownExampleValue(newValue);

                                setValue("exampleDropdown", newValue);
                                trigger(["exampleDropdown"]);
                            }}
                        />
                    </div>
                    {errors.exampleDropdown && (
                        <span style={{ color: "red" }}>
                            {errors.exampleDropdown.message}
                        </span>
                    )}
                </FlexRow>

                <div style={{ width: 300 }}>
                    <input type="submit" value="Send" />
                </div>
            </form>
        </Block>
    );
}

export default FormExamples;
